// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Hook for uploading documents with progress tracking.
 */
import { useCallback, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { NodeApiClient } from '@/app-shell/services'
import { uploadDocument } from '../services'
import type { DocumentUploadParams, CreateDocumentResponse } from '../types'
import { documentKeys } from './useDocuments'

/** Status of a file being uploaded */
export interface UploadingFile {
  id: string
  name: string
  size: number
  progress: number
  error?: string
  completed?: boolean
}

interface UseDocumentUploadParams {
  apiClient: NodeApiClient
  orgId: string
  wsId: string
  /** Called when upload completes successfully */
  onSuccess?: (response: CreateDocumentResponse) => void
  /** Called when upload fails */
  onError?: (error: Error, file: File) => void
}

interface UseDocumentUploadResult {
  /** Upload a single file */
  upload: (params: DocumentUploadParams) => Promise<CreateDocumentResponse | null>
  /** Upload multiple files */
  uploadMultiple: (files: File[], sharedParams?: Omit<DocumentUploadParams, 'file'>) => Promise<void>
  /** Files currently uploading */
  uploading: UploadingFile[]
  /** Whether any upload is in progress */
  isUploading: boolean
  /** Clear completed/errored uploads from the list */
  clearCompleted: () => void
}

/**
 * Upload documents with progress tracking and automatic cache invalidation.
 *
 * @example
 * ```tsx
 * const { upload, uploading, isUploading } = useDocumentUpload({
 *   apiClient,
 *   orgId: 'umd-org',
 *   wsId: 'dit-srm',
 *   onSuccess: () => toast.success('Upload complete'),
 * })
 *
 * // Single file with grouping
 * await upload({
 *   file: selectedFile,
 *   groupId: 'REQ-123456',
 *   slotId: 'dpq',
 * })
 *
 * // Multiple files
 * await uploadMultiple(files, { groupId: 'REQ-123456' })
 * ```
 */
export function useDocumentUpload({
  apiClient,
  orgId,
  wsId,
  onSuccess,
  onError,
}: UseDocumentUploadParams): UseDocumentUploadResult {
  const [uploading, setUploading] = useState<UploadingFile[]>([])
  const queryClient = useQueryClient()

  const upload = useCallback(
    async (params: DocumentUploadParams): Promise<CreateDocumentResponse | null> => {
      const file = params.file
      const uploadId = `${file.name}-${Date.now()}-${Math.random()}`

      // Add to uploading list
      setUploading((prev) => [
        ...prev,
        { id: uploadId, name: file.name, size: file.size, progress: 0 },
      ])

      try {
        const response = await uploadDocument(
          apiClient,
          orgId,
          wsId,
          params,
          (progress) => {
            setUploading((prev) =>
              prev.map((item) =>
                item.id === uploadId ? { ...item, progress } : item,
              ),
            )
          },
        )

        // Mark complete
        setUploading((prev) =>
          prev.map((item) =>
            item.id === uploadId ? { ...item, progress: 100, completed: true } : item,
          ),
        )

        // Auto-clear completed upload after 2 seconds
        setTimeout(() => {
          setUploading((prev) => prev.filter((item) => item.id !== uploadId))
        }, 2000)

        // Invalidate document list cache
        void queryClient.invalidateQueries({
          queryKey: documentKeys.lists(),
        })

        onSuccess?.(response)
        return response
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Upload failed')

        setUploading((prev) =>
          prev.map((item) =>
            item.id === uploadId ? { ...item, error: error.message } : item,
          ),
        )

        onError?.(error, file)
        return null
      }
    },
    [apiClient, orgId, wsId, queryClient, onSuccess, onError],
  )

  const uploadMultiple = useCallback(
    async (files: File[], sharedParams?: Omit<DocumentUploadParams, 'file'>) => {
      for (const file of files) {
        await upload({ file, ...sharedParams })
      }
    },
    [upload],
  )

  const clearCompleted = useCallback(() => {
    setUploading((prev) => prev.filter((item) => !item.completed && !item.error))
  }, [])

  return {
    upload,
    uploadMultiple,
    uploading,
    isUploading: uploading.some((item) => !item.completed && !item.error),
    clearCompleted,
  }
}
